import { Localization } from '@lglp/prismic-client';
import { invertObj, merge } from 'ramda';

import { getFocusPopinUidMapping } from '../../config/focusPopinUid';
import {
  getRouteKeyForPath,
  getRouteKeyOfChild,
  getUrlForRouteKey,
  ROUTE_KEY,
} from '../../config/routes';
import CHILD_ROUTE from '../../config/routes/child-route';
import { REGION_ROUTE_UID } from '../../config/routes/route';
import { DocumentType } from '../../types/document';
import { MEDIA_TYPE } from '../../types/press-media';
import { path } from '../compact';
import { EN, FR, getLang } from '../i18n';

const hotArticleRoute = ({ lang, uid } = {}) => {
  const lng = getLang(lang);
  return {
    routeKey: ROUTE_KEY.HOT_ARTICLE,
    params: { lng: lng, articleUid: uid },
  };
};

const coldArticleRoute = ({ lang, uid } = {}) => {
  const lng = getLang(lang);
  return {
    routeKey: ROUTE_KEY.COLD_ARTICLE,
    params: { lng: lng, articleUid: uid },
  };
};

const investorsRoute = ({ lang, uid } = {}) => {
  const lng = getLang(lang);
  return {
    routeKey: ROUTE_KEY.INVESTORS,
    params: { lng: lng, pageUid: uid },
  };
};

const hubRoute = ({ lang, uid } = {}) => {
  const lng = getLang(lang);

  let routeKey;

  switch (lng) {
    case FR:
      if (['publications'].includes(uid)) {
        routeKey = ROUTE_KEY.HUB_INVESTISSEURS;
      }
      if (['communiques-de-presse'].includes(uid)) {
        routeKey = ROUTE_KEY.HUB_INVESTISSEURS;
      }
      break;
    case EN:
      if (['publications'].includes(uid)) {
        routeKey = ROUTE_KEY.HUB_INVESTISSEURS;
      }
      if (['financial-press-release'].includes(uid)) {
        routeKey = ROUTE_KEY.HUB_INVESTISSEURS;
      }
      break;
    default:
      return undefined;
  }

  return {
    routeKey,
    params: { lng: lng, pageUid: uid },
  };
};

const focusPopinRoute = (media, routeInfo) => {
  const parent = getFocusPopinUidMapping(media.lang)[media.uid];
  if (!parent) {
    return '/';
  }
  if (parent.parentType === DocumentType.FOCUS) {
    return childRoute('popinUid', CHILD_ROUTE.FOCUS_POPIN.id, ROUTE_KEY.FOCUS, {
      lng: media.lng,
      focusUid: parent.parentUid,
      popinUid: media.uid,
    })(media, routeInfo);
  }

  return '/';
};
const focusRoute = ({ lang, uid } = {}) => {
  const lng = getLang(lang);
  return {
    routeKey: ROUTE_KEY.FOCUS,
    params: { lng: lng, focusUid: uid },
  };
};

const childRoute =
  (paramKey, childRouteKey, defaultParentRouteKey, params = {}) =>
  ({ uid, lang }, { match: { path } = {} } = {}) => {
    const routeKey = getRouteKeyForPath(path);
    return {
      routeKey: getRouteKeyOfChild(
        defaultParentRouteKey,
        childRouteKey,
        routeKey
      ),
      params: merge({ lng: getLang(lang), [paramKey]: uid }, params),
    };
  };

const getDefaultRouteOfMedia = ({ mediaType, onNewsroom, onFinance }) => {
  if (onFinance) {
    return mediaType === MEDIA_TYPE.PUBLICATION
      ? ROUTE_KEY.FINANCIAL_PUBLICATION_YEAR
      : ROUTE_KEY.FINANCIAL_PRESS_RELEASE_YEAR;
  }

  if (onNewsroom) {
    return ROUTE_KEY.NEWSROOM;
  }

  return ROUTE_KEY.NEWSROOM;
};

const getDefaultRouteOfPressRelease = ({ onNewsroom, onFinance }) => {
  if (onFinance) {
    return ROUTE_KEY.FINANCIAL_PRESS_RELEASE_YEAR;
  }

  if (onNewsroom) {
    return ROUTE_KEY.NEWSROOM;
  }

  return ROUTE_KEY.NEWSROOM;
};

const getRouteParamsForMedia = ({ onNewsroom, onFinance, relatedDate }) => {
  if (onFinance) {
    return { year: new Date(relatedDate).getFullYear() };
  }

  if (onNewsroom) {
    return {};
  }

  return {};
};

const getRouteParamsForPressRelease = ({
  onNewsroom,
  onFinance,
  release_date,
}) => {
  if (onFinance) {
    return { year: new Date(release_date).getFullYear() };
  }

  if (onNewsroom) {
    return {};
  }

  return {};
};

const mediaPressRoute = (media, routeInfo) =>
  childRoute(
    'mediaUid',
    CHILD_ROUTE.MEDIA.id,
    getDefaultRouteOfMedia(media),
    getRouteParamsForMedia(media)
  )(media, routeInfo);

const PressReleaseRoute = (pressRelease, routeInfo) =>
  childRoute(
    'pressReleaseUid',
    CHILD_ROUTE.PRESS_RELEASE.id,
    getDefaultRouteOfPressRelease(pressRelease),
    getRouteParamsForPressRelease(pressRelease)
  )(pressRelease, routeInfo);

const DOC_TO_ROUTE = {
  [DocumentType.HOT_ARTICLE]: hotArticleRoute,
  [DocumentType.COLD_ARTICLE]: coldArticleRoute,
  [DocumentType.INVESTORS]: investorsRoute,
  [DocumentType.MEDIA_PRESS]: mediaPressRoute,
  [DocumentType.PRESS_RELEASE]: PressReleaseRoute,
  [DocumentType.NEWSROOM]: () => ({ routeKey: ROUTE_KEY.NEWSROOM }),
  [DocumentType.FINANCIAL_DEBT]: () => ({ routeKey: ROUTE_KEY.FINANCIAL_DEBT }),
  [DocumentType.FINANCIAL_PUBLICATION]: (doc, routeInfo) => ({
    routeKey:
      path('match.params.year', routeInfo) &&
      path('match.params.year', routeInfo) != new Date(Date.now()).getFullYear()
        ? ROUTE_KEY.FINANCIAL_PUBLICATION_YEAR
        : ROUTE_KEY.FINANCIAL_PUBLICATION,
  }),
  [DocumentType.FINANCIAL_KEY_FIGURES]: () => ({
    routeKey: ROUTE_KEY.FINANCIAL_KEY_FIGURES,
  }),
  [DocumentType.FINANCIAL_PRESS_RELEASE]: (doc, routeInfo) => ({
    routeKey:
      path('match.params.year', routeInfo) &&
      path('match.params.year', routeInfo) != new Date(Date.now()).getFullYear()
        ? ROUTE_KEY.FINANCIAL_PRESS_RELEASE_YEAR
        : ROUTE_KEY.FINANCIAL_PRESS_RELEASE,
  }),
  [DocumentType.HOME]: (doc, routeInfo) => {
    const regionSlug = path('params.regionSlug', routeInfo);
    if (!regionSlug) {
      return {
        routeKey: ROUTE_KEY.HOME,
      };
    }
    return {
      routeKey: ROUTE_KEY.HOME_REGION,
      params: { regionSlug },
    };
  },
  [DocumentType.SEARCH]: () => ({ routeKey: ROUTE_KEY.SEARCH }),
  [DocumentType.HUB]: hubRoute,
  [DocumentType.HUB_ACTUS]: () => ({ routeKey: ROUTE_KEY.HUB_ACTU }),
  [DocumentType.HUB_NEWSROOM]: () => ({ routeKey: ROUTE_KEY.HUB_NEWSROOM }),
  [DocumentType.MEDIA_POPIN]: focusPopinRoute,
  [DocumentType.TIMELINE_POPIN]: focusPopinRoute,
  [DocumentType.SLIDER_POPIN]: focusPopinRoute,
  [DocumentType.VIDEO_POPIN]: focusPopinRoute,
  [DocumentType.FOCUS]: focusRoute,
};

export const getPopinUrlFromDocumentAndParent = (
  doc,
  routeInfo,
  keepFilter = false,
  parent
) => {
  const route = focusPopinRoute(doc, routeInfo, parent);

  if (!route) {
    return '#';
  }

  const routeParams = routeInfo?.match?.params || {};

  return [
    getUrlForRouteKey(route.routeKey, getLang(doc.lang), {
      ...routeParams,
      ...(route.params || {}),
    }),
    keepFilter && routeInfo?.location?.search,
  ]
    .filter(Boolean)
    .join('');
};

export const getUrlFromDocument = (doc, routeInfo, keepFilter = false) => {
  const toRoute = DOC_TO_ROUTE[doc?.type];
  const route = toRoute && toRoute(doc, routeInfo);

  if (!route) {
    return '#';
  }

  const routeParams = routeInfo?.match?.params || {};

  return [
    getUrlForRouteKey(route.routeKey, getLang(doc.lang), {
      ...routeParams,
      ...(route.params || {}),
    }),
    keepFilter && routeInfo?.location?.search,
  ]
    .filter(Boolean)
    .join('');
};

export const TYPE_TO_ROUTE_REGION = {
  [Localization.AUVERGNE]: REGION_ROUTE_UID.AUVERGNE,
  [Localization.BOURGOGNE]: REGION_ROUTE_UID.BOURGOGNE,
  [Localization.BRETAGNE]: REGION_ROUTE_UID.BRETAGNE,
  [Localization.CENTRE]: REGION_ROUTE_UID.CENTRE,
  [Localization.CORSE]: REGION_ROUTE_UID.CORSE,
  [Localization.DOM]: REGION_ROUTE_UID.DOM,
  [Localization.GRAND_EST]: REGION_ROUTE_UID.GRAND_EST,
  [Localization.HAUTS_DE_FRANCE]: REGION_ROUTE_UID.HAUTS_DE_FRANCE,
  [Localization.ILE_DE_FRANCE]: REGION_ROUTE_UID.ILE_DE_FRANCE,
  [Localization.National]: REGION_ROUTE_UID.NATIONAL,
  [Localization.NORMANDIE]: REGION_ROUTE_UID.NORMANDIE,
  [Localization.NOUVELLE_AQUITAINE]: REGION_ROUTE_UID.NOUVELLE_AQUITAINE,
  [Localization.OCCITANIE]: REGION_ROUTE_UID.OCCITANIE,
  [Localization.PAYS_DE_LA_LOIRE]: REGION_ROUTE_UID.PAYS_DE_LA_LOIRE,
  [Localization.PACA]: REGION_ROUTE_UID.PACA,
  [Localization.International]: REGION_ROUTE_UID.INTERNATIONAL,
};

export const routeToTypeRegion = (route) =>
  invertObj(TYPE_TO_ROUTE_REGION)[route];
